import {
  ANIMAL_INACTIVE_TAG_LABEL,
  HEALTH_NOTE_BADGE_LABEL,
  ROUTINE_CARE_BADGE_LABEL,
  composeAnimalAriaLabel,
} from "../lib/recordsCopy";
import type { AnimalOut } from "../types";
import { AnimalAvatar } from "./SpeciesAvatar";

export interface AnimalCardProps {
  animal: AnimalOut;
  /** `useAnimalHealthNotes()`'s 14-day-window signal — the SAME hook/
   * fan-out both callers already share, unchanged by this consolidation.
   * Callers pass `recentNoteLevel(id) === "attention"`: only events that
   * need someone to look at the animal get the clay badge
   * (`lib/healthNotes.ts`). */
  hasHealthNote: boolean;
  /** A recent event exists but it is routine care. Renders a neutral
   * "Routine care" chip instead of the clay badge; ignored when
   * `hasHealthNote` is true. Optional (default `false`). */
  hasRoutineCare?: boolean;
  onOpen: () => void;
  /**
   * Photo case (design-spec-animal-cards-2026-07-10.md §2, CD blocking
   * change #1). Sprint-013 B2 (SA ruling Decision 4.3, backend gap #5
   * closed) — both current callers (`AnimalsView`'s full grid,
   * `DashboardView`'s mini-grid) derive this from
   * `animal.primary_photo_attachment_id` via `attachmentThumbUrl()`, a
   * field the LIST endpoint now resolves server-side with no per-card
   * fetch (`GET /animals` batches one "newest photo per animal" query —
   * see the SA ruling — so this consolidation still never re-adds the
   * N+1 fan-out this component's shape was deliberately built to avoid).
   * `undefined` (no resolved photo) falls back to the species silhouette;
   * a broken/deleted attachment also falls back, via `AnimalAvatar`'s own
   * `<img onError>` handling, never a broken-image icon.
   */
  photoUrl?: string;
  /**
   * design-spec §0 "Inactive-animal treatment" — the CSS/markup is built
   * (background-only `--paper` tint + pill tag, never `opacity`) but per
   * the spec's own handoff checklist this must not be wired to real
   * `animal.active === false` data until PM resolves UX gap #8 (whether
   * inactive animals are even in-scope for this grid). Both current
   * callers pass `false`/omit this prop; do not change that without a
   * PM decision landing first.
   */
  inactive?: boolean;
  /**
   * Sprint-016 F3 (DASH-01, ux-audit-whole-app-2026-07-11.md — S3, CD
   * binding condition 5). Default `true` — every pre-existing caller
   * (`AnimalsView`'s real full-page grid) is UNCHANGED. `DashboardView`'s
   * mini-grid is the one caller passing `false`: its click-through has
   * always routed to the generic `/animals` list, never this exact
   * animal's own detail overlay (see this component's shared-usage
   * docstring below), so the chevron/button affordance was over-
   * promising "open this exact animal." `false` renders a plain,
   * non-`<button>` `<div>` — no `onClick`, no button-role `aria-label`,
   * no chevron — a genuinely static preview row, never a fake-disabled
   * button (`role="button"` is never added here).
   */
  interactive?: boolean;
}

/**
 * design-spec-animal-cards-2026-07-10.md §2 — Option B (avatar +
 * status-chip row), Creative-Director-approved-with-changes 2026-07-10.
 * The single shared card both `AnimalsView` (full Animals-page grid) and
 * `DashboardView`'s Animals-snapshot mini-grid render — previously two
 * copy-pasted implementations (UX §1.1's consolidation ask), now one.
 *
 * Card markup/class names deliberately keep the pre-existing
 * `.animal-card`/`.animal-card-name` selectors (only the CSS box model
 * changes from a column tile to a row) so the app's e2e suite — which
 * targets those class names directly (`e2e/tests/records/
 * animals-journey.spec.ts`, `e2e/tests/desktop/dashboard.spec.ts`,
 * `e2e/tests/roles/{zero-jargon,axe-scan}.spec.ts`) — keeps working
 * unchanged.
 */
export function AnimalCard({
  animal,
  hasHealthNote,
  hasRoutineCare = false,
  onOpen,
  photoUrl,
  inactive = false,
  interactive = true,
}: AnimalCardProps) {
  const showRoutine = hasRoutineCare && !hasHealthNote;
  const ariaLabel = composeAnimalAriaLabel(animal, {
    hasHealthNote,
    inactive,
    hasRoutineCare: showRoutine,
  });
  const cardClass = `animal-card${hasHealthNote ? " has-health-note" : ""}${inactive ? " is-inactive" : ""}${interactive ? "" : " is-static"}`;

  const content = (
    <>
      <AnimalAvatar species={animal.species} photoUrl={photoUrl} />
      <span class="animal-card-main">
        <span class="animal-card-name-row">
          <span class="animal-card-name">{animal.name}</span>
          {/* CD ruling, 2026-07-10 (final, not a hedge): when both states
           * apply, the inactive chip renders first, health-note chip
           * second — status takes visual priority over the time-bound
           * health flag. This is the DOM/visual order only; the
           * `aria-label` above appends health-note before inactive per
           * §0's own bullet order — the two orderings are intentionally
           * independent. */}
          {inactive ? (
            <span class="animal-card-chip is-inactive">{ANIMAL_INACTIVE_TAG_LABEL}</span>
          ) : null}
          {hasHealthNote ? (
            <span class="animal-card-chip animal-card-note-badge">{HEALTH_NOTE_BADGE_LABEL}</span>
          ) : null}
          {showRoutine ? (
            <span class="animal-card-chip animal-card-routine-badge">
              {ROUTINE_CARE_BADGE_LABEL}
            </span>
          ) : null}
        </span>
        {animal.species ? <span class="animal-card-species">{animal.species}</span> : null}
      </span>
      {/* §2 "Accessibility notes specific to B" — decorative; the card's
       * own composed `aria-label` above is the accessible name. Sprint-016
       * F3 (DASH-01) — suppressed entirely when `interactive` is false:
       * the chevron promises "opens this exact animal," which is no
       * longer true for a static preview row (CD binding condition 5). */}
      {interactive ? (
        <span class="animal-card-chevron" aria-hidden="true">
          ›
        </span>
      ) : null}
    </>
  );

  // Sprint-016 F3 (DASH-01, ux-audit-whole-app-2026-07-11.md — S3, CD
  // binding condition 5) — `interactive={false}` (Dashboard's mini-grid
  // only; `AnimalsView`'s real grid never passes it) renders a plain,
  // non-`<button>` wrapper: no `onClick`, no button-role `aria-label`,
  // never `role="button"` (a genuinely static content row, not a
  // fake-disabled control).
  if (!interactive) {
    return <div class={cardClass}>{content}</div>;
  }

  return (
    <button type="button" class={cardClass} onClick={onOpen} aria-label={ariaLabel}>
      {content}
    </button>
  );
}
